// Filter row for Bank account history.
// Funnel icon + chevron ▼ opens presets dropdown.
// Download icon opens a format dropdown (CSV / Excel).
// Settings gear opens settings popover (columns, modes, rows per page).

import React, { useEffect, useRef, useState } from "react";
import { Search, Download, Printer, SlidersHorizontal } from "lucide-react";
import type { BankHistoryFilter } from "../types";
import { DEFAULT_BANK_HISTORY_FILTER, type BankHistorySettings } from "../constants";
import BankHistoryFilterPanel from "./BankHistoryFilterPanel";

export type ExportFormat = "csv" | "xlsx";

interface BankHistoryFilterBarProps {
  query: string;
  onQueryChange: (q: string) => void;
  filter: BankHistoryFilter;
  onFilterChange: (f: BankHistoryFilter) => void;
  payees: string[];
  settings: BankHistorySettings;
  onSettingsChange: (next: BankHistorySettings) => void;
  onExport: (format: ExportFormat) => void;
  onPrint: () => void;
}

type PresetKey = "all" | "reconciled" | "cleared" | "uncleared" | "deposits" | "payments";

function getActivePreset(f: BankHistoryFilter): PresetKey | null {
  const noOthers = f.payee === "all" && f.datePreset === "all" && !f.find && !f.fromDate && !f.toDate;
  if (!noOthers) return null;
  if (f.transactionType === "all") {
    if (f.reconcileStatus === "all") return "all";
    if (f.reconcileStatus === "reconciled") return "reconciled";
    if (f.reconcileStatus === "cleared") return "cleared";
    if (f.reconcileStatus === "unreconciled") return "uncleared";
  }
  if (f.reconcileStatus === "all") {
    if (f.transactionType === "deposit") return "deposits";
    if (f.transactionType === "payment") return "payments";
  }
  return null;
}

function presetLabel(key: PresetKey): string {
  switch (key) {
    case "all": return "All";
    case "reconciled": return "Reconciled";
    case "cleared": return "Cleared";
    case "uncleared": return "Uncleared";
    case "deposits": return "Deposits only";
    case "payments": return "Payments only";
  }
}

const TOOLTIP_WRAP_CLASS =
  "absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 opacity-0 group-hover:opacity-100 " +
  "transition-opacity bg-ink text-white text-[11px] font-semibold px-2 py-1 rounded-md " +
  "whitespace-nowrap pointer-events-none z-50";

const TOOLTIP_ARROW_CLASS =
  "absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-ink";

const ICON_BUTTON_CLASS =
  "w-8 h-8 rounded-md bg-white border border-line text-ink3 inline-flex items-center " +
  "justify-center cursor-pointer hover:bg-[#FAFBFB]";

export default function BankHistoryFilterBar(props: BankHistoryFilterBarProps) {
  const [filterOpen, setFilterOpen] = useState(false);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const funnelRef = useRef<HTMLDivElement>(null);
  const downloadRef = useRef<HTMLDivElement>(null);
  const settingsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (presetsOpen && funnelRef.current && !funnelRef.current.contains(e.target as Node)) setPresetsOpen(false);
      if (downloadOpen && downloadRef.current && !downloadRef.current.contains(e.target as Node)) setDownloadOpen(false);
      if (settingsOpen && settingsRef.current && !settingsRef.current.contains(e.target as Node)) setSettingsOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [presetsOpen, downloadOpen, settingsOpen]);

  const activePreset = getActivePreset(props.filter);
  const chipLabel = activePreset ? presetLabel(activePreset) : "Filtered";

  const applyPreset = (key: PresetKey) => {
    const next: BankHistoryFilter = { ...DEFAULT_BANK_HISTORY_FILTER };
    switch (key) {
      case "reconciled": next.reconcileStatus = "reconciled"; break;
      case "cleared": next.reconcileStatus = "cleared"; break;
      case "uncleared": next.reconcileStatus = "unreconciled"; break;
      case "deposits": next.transactionType = "deposit"; break;
      case "payments": next.transactionType = "payment"; break;
      case "all": break;
    }
    props.onFilterChange(next);
    setPresetsOpen(false);
  };

  const openAdvanced = () => {
    setPresetsOpen(false);
    setFilterOpen(true);
  };

  const handleDownload = (format: ExportFormat) => {
    setDownloadOpen(false);
    props.onExport(format);
  };

  return (
    <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
      <div className="flex items-center gap-3 flex-1">
        <div ref={funnelRef} className="relative">
          <button
            type="button"
            onClick={() => setPresetsOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold bg-transparent border-none cursor-pointer px-2 py-1.5 rounded-md hover:bg-panel"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="text-ink3">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            <span className="text-[9px] text-ink4" aria-hidden="true">▼</span>
            <span className="text-ink">{chipLabel}</span>
          </button>

          {presetsOpen && (
            <div className="absolute left-0 top-full mt-1 w-[220px] bg-white border border-line rounded-lg shadow-[0_8px_24px_rgba(14,26,26,0.12)] py-1 z-40 text-left">
              <PresetItem label="All" active={activePreset === "all"} onClick={() => applyPreset("all")} />
              <PresetItem label="Reconciled" active={activePreset === "reconciled"} onClick={() => applyPreset("reconciled")} />
              <PresetItem label="Cleared, not reconciled" active={activePreset === "cleared"} onClick={() => applyPreset("cleared")} />
              <PresetItem label="Uncleared" active={activePreset === "uncleared"} onClick={() => applyPreset("uncleared")} />
              <div className="h-px bg-lineSoft my-1" />
              <PresetItem label="Deposits only" active={activePreset === "deposits"} onClick={() => applyPreset("deposits")} />
              <PresetItem label="Payments only" active={activePreset === "payments"} onClick={() => applyPreset("payments")} />
              <div className="h-px bg-lineSoft my-1" />
              <button type="button" onClick={openAdvanced} className="w-full text-left px-3 py-2 text-[12.5px] font-semibold text-teal-dark bg-transparent border-none cursor-pointer hover:bg-panel">
                Advanced filters…
              </button>
            </div>
          )}

          <BankHistoryFilterPanel
            open={filterOpen}
            onClose={() => setFilterOpen(false)}
            initial={props.filter}
            onApply={props.onFilterChange}
            payees={props.payees}
          />
        </div>

        <div className="relative flex-1 md:max-w-[340px]">
          <Search size={14} strokeWidth={2} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink4 pointer-events-none" />
          <input
            value={props.query}
            onChange={(e) => props.onQueryChange(e.target.value)}
            placeholder="Filter by payee, amount, or reference"
            className="w-full bg-white border border-line rounded-full px-10 py-2 text-[12.5px] text-ink placeholder:text-ink4 outline-none focus:border-teal-dark"
          />
        </div>
      </div>

      <div className="flex items-center gap-1.5">
        <div ref={downloadRef} className="relative">
          <IconButton tooltip="Download" onClick={() => setDownloadOpen((v) => !v)}>
            <Download size={14} strokeWidth={2} />
          </IconButton>
          {downloadOpen && (
            <div className="absolute right-0 top-full mt-1 w-[240px] bg-white border border-line rounded-xl shadow-[0_8px_24px_rgba(14,26,26,0.14)] py-1 z-40 text-left">
              <div className="px-3 py-1.5 text-[10px] text-ink3 uppercase tracking-wider font-bold">Download</div>
              <DownloadItem title="CSV (.csv)" hint="Spreadsheets, text editors" onClick={() => handleDownload("csv")} />
              <DownloadItem title="Excel (.xlsx)" hint="Formatted" onClick={() => handleDownload("xlsx")} />
            </div>
          )}
        </div>
        <IconButton tooltip="Print" onClick={props.onPrint}>
          <Printer size={14} strokeWidth={2} />
        </IconButton>
        <div ref={settingsRef} className="relative">
          <IconButton tooltip="Settings" onClick={() => setSettingsOpen((v) => !v)}>
            <SlidersHorizontal size={14} strokeWidth={2} />
          </IconButton>
          {settingsOpen && (
            <SettingsPopover settings={props.settings} onChange={props.onSettingsChange} />
          )}
        </div>
      </div>
    </div>
  );
}

function DownloadItem({ title, hint, onClick }: { title: string; hint: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="w-full text-left px-3 py-2 bg-transparent border-none cursor-pointer hover:bg-panel flex items-center justify-between">
      <span className="text-[12.5px] text-ink">{title}</span>
      <span className="text-[10px] text-ink4">{hint}</span>
    </button>
  );
}

function SettingsPopover({ settings, onChange }: { settings: BankHistorySettings; onChange: (s: BankHistorySettings) => void }) {
  const setColumn = (key: keyof BankHistorySettings["columns"], value: boolean) => {
    onChange({ ...settings, columns: { ...settings.columns, [key]: value } });
  };
  const setFlag = <K extends "showInOneLine" | "paperLedgerMode" | "compact">(key: K, value: boolean) => {
    onChange({ ...settings, [key]: value });
  };
  const setRows = (rows: 50 | 100 | 200) => {
    onChange({ ...settings, rowsPerPage: rows });
  };

  return (
    <div className="absolute right-0 top-full mt-1 w-[260px] bg-white border border-line rounded-xl shadow-[0_8px_24px_rgba(14,26,26,0.14)] p-4 z-40 text-left">
      <div className="text-[11px] text-ink3 uppercase tracking-wider font-bold mb-3">Columns</div>
      <SettingRow label="Memo" checked={settings.columns.memo} onChange={(v) => setColumn("memo", v)} />
      <SettingRow label="Reconcile and banking status" checked={settings.columns.reconcileStatus} onChange={(v) => setColumn("reconcileStatus", v)} />
      <SettingRow label="Attachments" checked={settings.columns.attachments} onChange={(v) => setColumn("attachments", v)} />
      <SettingRow label="Running balance" checked={settings.columns.runningBalance} onChange={(v) => setColumn("runningBalance", v)} />

      <div className="h-px bg-line my-3" />
      <div className="text-[11px] text-ink3 uppercase tracking-wider font-bold mb-2">Other</div>
      <SettingRow label="Show in one line" checked={settings.showInOneLine} onChange={(v) => setFlag("showInOneLine", v)} />
      <SettingRow label="Paper ledger mode" checked={settings.paperLedgerMode} onChange={(v) => setFlag("paperLedgerMode", v)} />
      <SettingRow label="Compact" checked={settings.compact} onChange={(v) => setFlag("compact", v)} />

      <div className="h-px bg-line my-3" />
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-ink3 uppercase tracking-wider font-bold">Rows</div>
        <select
          value={settings.rowsPerPage}
          onChange={(e) => setRows(Number(e.target.value) as 50 | 100 | 200)}
          className="bg-white border border-line rounded-full px-3 py-1 text-[12px] text-ink outline-none focus:border-teal-dark"
          style={{ height: 30, width: 90 }}
        >
          <option value={50}>50</option>
          <option value={100}>100</option>
          <option value={200}>200</option>
        </select>
      </div>
    </div>
  );
}

function SettingRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-[13px] text-ink py-1.5 cursor-pointer select-none">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="cursor-pointer" />
      {label}
    </label>
  );
}

function PresetItem({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="w-full text-left px-3 py-2 text-[12.5px] bg-transparent border-none cursor-pointer hover:bg-panel inline-flex items-center justify-between text-ink">
      <span>{label}</span>
      {active && <span className="text-teal-dark text-[12px] font-bold">✓</span>}
    </button>
  );
}

function IconButton({ tooltip, onClick, children }: { tooltip: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <div className="relative group">
      <button type="button" onClick={onClick} className={ICON_BUTTON_CLASS} aria-label={tooltip}>
        {children}
      </button>
      <span className={TOOLTIP_WRAP_CLASS}>
        {tooltip}
        <span className={TOOLTIP_ARROW_CLASS} />
      </span>
    </div>
  );
}